import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/**
 * Three-vertical-dots action menu used in table rows.
 * Pass `items` as an array of { label, icon, onClick, danger?, disabled? }.
 * The menu auto-closes on outside click or Escape.
 *
 * The dropdown is portaled to document.body so it can escape any
 * `overflow: hidden` ancestors (cards, table containers, etc).
 */
const KebabMenu = ({ items = [], className = "" }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Right-align under the trigger; min-width is 160px (set in CSS).
    setPosition({
      top: rect.bottom + 4,
      left: rect.right - 160,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (e) => {
      const inTrigger =
        triggerRef.current && triggerRef.current.contains(e.target);
      const inDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleScroll = () => setOpen(false);

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [open]);

  const dropdown = open ? (
    <ul
      ref={dropdownRef}
      className="kebab-dropdown kebab-dropdown-portal"
      role="menu"
      style={{
        position: "fixed",
        top: position.top,
        left: Math.max(8, position.left),
      }}
    >
      {items.map((item, idx) => {
        if (!item) return null;
        const cls = `kebab-item${item.danger ? " danger" : ""}${
          item.disabled ? " disabled" : ""
        }`;
        return (
          <li key={`kebab-${idx}`} role="none">
            <button
              type="button"
              role="menuitem"
              className={cls}
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                if (typeof item.onClick === "function") item.onClick();
              }}
            >
              {item.icon && <i className={item.icon} />}
              <span>{item.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      className={`kebab-menu ${open ? "open" : ""} ${className}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="kebab-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Actions"
      >
        <i className="fas fa-ellipsis-v" />
      </button>

      {dropdown && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
};

export default KebabMenu;
